import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, Ruler, FileCheck, Users, Sparkles, ShieldCheck, MapPin, Clock, BookOpen, CreditCard } from 'lucide-react';
import { Seo } from '../lib/Seo';
import { pageSeo, pageSchema } from '../seo/staticPages';
import { COMPANY, IMAGES, SOUTH_SOUND_CITIES, NORTH_SOUND_CITIES, BEFORE_AFTER } from '../lib/siteConfig';
import { Section, SectionTitle, TrustBar, CtaBand, Reveal, Watermark, CityTicker, HeroPicture } from '../components/Shared';
import { ARTICLE_INDEX, articlePath } from '../data/blogIndex'; // light registry — keeps the article bodies out of the homepage bundle
import { ServiceRadiusMap } from '../components/ServiceRadiusMap';
import { trackCall } from '../lib/analytics';
import { srcSetFor, dims, SIZES } from '../lib/img';
import { HOME_BAND_TEXT } from '../config/financing';
import { FinancingLink, LenderButtons } from '../components/financing/Lenders';

const SEGMENTS = [
  { title: 'Residential Installation', to: '/residential-hvac-installation', img: IMAGES.residential, alt: 'Technician installing a residential heat pump system', desc: 'Heat pumps, ductless mini-splits, furnaces, and AC — sized with a real Manual J load calculation, never a guess.' },
  { title: 'Commercial Installation', to: '/commercial-hvac-installation', img: IMAGES.commercial, alt: 'Commercial rooftop HVAC units on an industrial building', desc: 'RTUs, VRF, make-up air, and tenant improvements — bid from plans, delivered on your schedule.' },
  { title: 'Industrial / Mechanical', to: '/industrial-hvac-installation', img: IMAGES.industrial, alt: 'Rows of large-tonnage cooling equipment on an industrial rooftop', desc: 'Large-tonnage systems and process ventilation inside operating facilities, with minimal downtime.' },
  { title: 'Emergency Service', to: '/emergency-hvac-service', img: IMAGES.emergency, alt: 'Technician arriving at a home at night for an urgent no-heat call', desc: 'No heat in a cold snap? No cooling in a heat wave? Genuine emergencies jump the schedule — call first, we answer.', urgent: true },
];

const WHY = [
  { icon: Ruler, title: 'Sized with real math', desc: 'Every system starts with a room-by-room Manual J load calculation — never a square-footage guess.' },
  { icon: FileCheck, title: 'Permit and code fluency', desc: 'Every install is permitted, inspected, and documented to IRC, IMC, and WSEC. No shortcuts.' },
  { icon: Users, title: 'In-house installers', desc: 'The people who quote your job are the people who build it — no subcontractor pool.' },
  { icon: Sparkles, title: 'Clean job sites', desc: 'Floor protection in, daily cleanup, full haul-away. The only evidence we were there is a system that works.' },
  { icon: ShieldCheck, title: 'Workmanship warranty', desc: 'Manufacturer warranties cover the box. Ours covers everything we did to install it.' },
];

const HERO_STATS = [
  { label: 'License', value: `WA #${COMPANY.license}` },
  { label: 'Coverage', value: 'Puget Sound' },
  { label: 'Specialty', value: 'Heat Pumps' },
  { label: 'Response', value: '1 Business Day' },
];

// Hoisted (module scope) so these aren't re-created on every render.
const TICKER_CITIES = [...SOUTH_SOUND_CITIES.slice(0, 10), ...NORTH_SOUND_CITIES.slice(0, 8)];
const GUIDE_IMAGES = [IMAGES.residential, IMAGES.mechanicalRoom, IMAGES.darkIndustrial];

const TeaserOption = ({ value, label }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/estimate-calculator?property_type=${value}`)}
      data-testid={`home-teaser-${value}`}
      className="text-left rounded-[12px] border border-[#D7D2C8] bg-[#FFFCF6] hover:border-[#3E6F8E]/70 hover:bg-white px-4 py-3.5 font-medium text-[15px] text-[#121417] transition-colors duration-200 flex items-center justify-between gap-2 group"
    >
      {label}
      <ArrowRight className="h-4 w-4 text-[#3E6F8E] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </button>
  );
};

export default function HomePage() {
  return (
    <>
      <Seo
        {...pageSeo('/')}
        schema={pageSchema('/')}
      />

      {/* HERO — dark atmospheric canvas + blueprint grid + grain */}
      <div className="relative bg-[#121417] idb-grain overflow-hidden" data-testid="home-hero">
        <HeroPicture src={IMAGES.heroHome} originalWidth={1920} alt="Puget Sound craftsman home at dusk with warm interior lighting after a completed HVAC installation" className="absolute inset-0 h-full w-full object-cover" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121417]/95 via-[#121417]/75 to-[#121417]/40" />
        <div className="absolute inset-0 idb-blueprint idb-blueprint-dark pointer-events-none" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-28 lg:pt-32 pb-12 sm:pb-20">
          {/* SEO: the <h1> carries the service + place; the tagline stays the visual headline. */}
          <h1 className="font-mono-idb text-xs sm:text-sm uppercase tracking-[0.18em] text-[rgba(244,241,234,0.72)]" data-testid="home-hero-h1">Heat Pump &amp; HVAC Installation in the Puget Sound</h1>
          <p className="mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-white leading-[1.05]" data-testid="home-hero-tagline">
            Installed right.{' '}
            <span className="underline decoration-[rgba(208,122,74,0.85)] decoration-2 underline-offset-8">Sized right.</span>{' '}
            Guaranteed.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="idb-gradient-rule w-24" />
            <div className="idb-dimension-rule idb-dimension-rule-dark flex-1 max-w-[180px]" />
          </div>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[rgba(244,241,234,0.86)]">
            Complete heating and cooling system installations for homes, businesses, and industrial facilities across the Puget Sound — by installers who run the load math, pull the permits, and stand behind the work.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link to="/estimate-calculator" data-testid="hero-pre-estimate-cta" className="h-12 px-6 inline-flex items-center justify-center rounded-xl idb-btn-copper text-sm active:scale-[0.98]">
              Get Your Instant Pre-Estimate
            </Link>
            <a href={COMPANY.phoneMainHref} onClick={() => trackCall('hero')} data-testid="hero-call-cta" className="h-12 px-6 inline-flex items-center justify-center gap-2 rounded-xl border border-[rgba(244,241,234,0.22)] text-white text-sm font-medium hover:border-[rgba(244,241,234,0.38)] hover:bg-[rgba(255,252,246,0.06)] transition-colors duration-200">
              <Phone className="h-4 w-4 text-[#7FA7B8]" /> Call {COMPANY.phoneMain}
            </a>
          </div>

          {/* Spec-plate stat strip */}
          <div className="mt-8 sm:mt-12 rounded-2xl border border-[rgba(244,241,234,0.14)] bg-[rgba(27,32,38,0.55)] backdrop-blur-md p-4 sm:p-5 idb-brackets-dark" data-testid="hero-stat-strip">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {HERO_STATS.map((s) => (
                <div key={s.label} className="min-w-[120px]" data-testid={`hero-stat-${s.label.toLowerCase()}`}>
                  <p className="font-mono-idb text-[11px] max-md:text-xs uppercase tracking-[0.16em] text-[rgba(244,241,234,0.62)]">{s.label}</p>
                  <p className="mt-1 font-display text-base sm:text-lg text-[rgba(244,241,234,0.92)]">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 idb-gradient-rule rounded-none z-10" />
      </div>
      <TrustBar withRating />

      {/* 01 — SEGMENTS (bento) */}
      <Section className="overflow-hidden" data-testid="home-segments">
        <Watermark num="01" />
        <SectionTitle title="One crew standard. Every market." sub="From a two-bedroom heat pump conversion in Tacoma to a six-RTU tenant improvement in Kent — plus priority emergency response when a system goes down — the process is the same: engineered sizing, code-fluent installation, clean closeout." />
        <div className="mt-10 max-md:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5 max-md:gap-4">
          {SEGMENTS.map((s, i) => (
            <Reveal key={s.to} delay={i * 0.07}>
              <Link to={s.to} data-testid={`segment-card-${i}`} className="group relative block overflow-hidden rounded-[16px] border border-[#D7D2C8] bg-[#121417] h-full min-h-[260px] max-md:min-h-[200px] idb-card-hover">
                <img src={s.img} srcSet={srcSetFor(s.img)} sizes={SIZES.quarter} {...dims(s.img)} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-[1.02] transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121417] via-[#121417]/40 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-6 lg:p-8 max-md:p-5">
                  <h3 className="font-display text-xl lg:text-2xl font-semibold text-white">{s.title}</h3>
                  {/* Mobile: two-line clamp keeps each market card near 200px (mobile polish §6). */}
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-[#C9D2DA] max-md:line-clamp-2">{s.desc}</p>
                  <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-medium ${s.urgent ? 'text-[#E7C9A6]' : 'text-[#7FA7B8]'}`}>{s.urgent ? 'Get help now' : `Explore ${s.title.split(' ')[0].toLowerCase()}`} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 02 — HOW THE PRE-ESTIMATE WORKS + teaser (dark break) */}
      <Section className="bg-[#1B2026] idb-grain idb-blueprint idb-blueprint-dark overflow-hidden" data-testid="home-how-it-works">
        <div className="absolute top-0 inset-x-0 idb-gradient-hairline z-10" />
        <Watermark num="02" dark />
        <div className="grid lg:grid-cols-2 gap-12 max-md:gap-8 items-center">
          <div>
            <SectionTitle dark title="Know your number before anyone visits" sub="Most contractors make you book a sales visit to hear a price. Our pre-estimate calculator gives you an honest planning range first — built on the same pricing logic we use in the field." />
            <ol className="mt-8 max-md:mt-6 space-y-6 max-md:space-y-5">
              {[
                ['Tell us about your building', 'Ten quick questions — size, fuel, ductwork, goals. About two minutes, no contact info until the end.'],
                ['Get your instant range', 'A personalized planning range with what’s included and what could move the number. Never a fake single price.'],
                ['We confirm with an on-site measure', 'A free visit with a real load calculation turns your range into a firm, signed quote.'],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="h-9 w-9 shrink-0 rounded-full border border-[#2A313A] bg-[#121417] font-mono-idb text-sm text-[#E7C9A6] flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="font-display font-semibold text-white">{t}</p>
                    <p className="mt-1 text-sm leading-relaxed text-[#8FA0AE]">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <Reveal>
            <div className="relative rounded-[18px] bg-[#FFFCF6] border border-[#D7D2C8] shadow-[var(--idb-shadow-md)] p-6 sm:p-8 idb-brackets lg:-translate-y-4" data-testid="home-calculator-teaser">
              <span className="idb-spec">Step 01 / 11</span>
              <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden"><div className="h-full w-[9%] rounded-full" style={{ background: 'var(--idb-heatcool)' }} /></div>
              <h3 className="mt-5 font-display text-xl font-semibold text-[#121417]">What type of property is this?</h3>
              <p className="mt-1 text-sm text-[#61676E]">Start here — your answers shape the estimate.</p>
              <div className="mt-4 idb-dimension-rule" />
              <div className="mt-5 grid gap-3">
                <TeaserOption value="single_family" label="Single-family home" />
                <TeaserOption value="townhome_condo" label="Townhome or condo" />
                <TeaserOption value="multi_family" label="Multi-family building" />
                <TeaserOption value="commercial_industrial" label="Commercial or industrial building" />
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 03 — WHY IDB */}
      <Section className="overflow-hidden" data-testid="home-why">
        <Watermark num="03" />
        <SectionTitle title="Trades-first. Not a call center." sub="We built this company from the installer’s side of the trade. These are the five things we believe separate a system that performs for fifteen years from one you fight with every winter." />
        <div className="mt-10 max-md:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-md:gap-3">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06}>
              {/* Under 768px: compact row card — icon tile left, title + copy right, counter beside the
                  title — roughly half the height of the stacked desktop card (mobile polish §6). The
                  desktop DOM/classes are unchanged from md up. */}
              <div className={`idb-card idb-card-hover p-6 h-full max-md:p-4 max-md:flex max-md:gap-3.5 ${i === 0 ? 'idb-brackets' : ''}`}>
                <div className="flex items-center justify-between max-md:block max-md:shrink-0">
                  <span className="max-md:inline-flex max-md:h-10 max-md:w-10 max-md:items-center max-md:justify-center max-md:rounded-[10px] max-md:bg-[#3E6F8E]/10">
                    <w.icon className="h-6 w-6 max-md:h-5 max-md:w-5 text-[#3E6F8E]" />
                  </span>
                  <span className="font-mono-idb text-[10px] uppercase tracking-[0.14em] text-[#6E6759] max-md:hidden">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="max-md:min-w-0 max-md:flex-1">
                  <h3 className="mt-4 max-md:mt-0 font-display font-semibold text-[#121417] max-md:flex max-md:items-start max-md:justify-between max-md:gap-3">
                    {w.title}
                    <span className="md:hidden font-mono-idb text-xs font-normal uppercase tracking-[0.14em] text-[#6E6759] shrink-0 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  </h3>
                  <div className="mt-3 idb-dimension-rule max-md:hidden" />
                  <p className="mt-3 max-md:mt-1 text-sm leading-relaxed max-md:leading-snug text-[#454B52]">{w.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="rounded-[14px] bg-[#121417] p-6 max-md:p-5 h-full flex flex-col justify-between idb-grain idb-brackets-dark relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 idb-gradient-rule rounded-none" />
              <p className="relative font-display font-semibold text-white text-lg">Sized right. Installed clean. Priced straight.</p>
              <Link to="/about" className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#7FA7B8] hover:text-white transition-colors duration-200 max-md:min-h-11" data-testid="home-about-link">Meet the company <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 04 — PROOF (slim before/after teaser) */}
      <Section className="bg-[#EDE9E0] overflow-hidden" data-testid="home-projects">
        <Watermark num="04" />
        <div className="grid lg:grid-cols-[1fr,400px] gap-10 max-md:gap-6 items-center">
          <Reveal>
            <div className="idb-card idb-card-hover idb-brackets overflow-hidden" data-testid="home-before-after-teaser">
              <div className="grid grid-cols-2 gap-px bg-[#D7D2C8]">
                <div className="relative">
                  <img src={BEFORE_AFTER[0].before} srcSet={srcSetFor(BEFORE_AFTER[0].before)} sizes={SIZES.half} {...dims(BEFORE_AFTER[0].before)} alt={BEFORE_AFTER[0].beforeAlt} loading="lazy" className="h-52 sm:h-72 w-full object-cover" />
                  <span className="absolute top-3 left-3 idb-spec idb-spec-dark bg-[#121417]/80 backdrop-blur-sm">Before</span>
                </div>
                <div className="relative">
                  <img src={BEFORE_AFTER[0].after} srcSet={srcSetFor(BEFORE_AFTER[0].after)} sizes={SIZES.half} {...dims(BEFORE_AFTER[0].after)} alt={BEFORE_AFTER[0].afterAlt} loading="lazy" className="h-52 sm:h-72 w-full object-cover" />
                  <span className="absolute top-3 left-3 idb-spec bg-[#FFFCF6]/90 backdrop-blur-sm text-[#2F6B57] border-[#2F6B57]/40">After</span>
                </div>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionTitle title="Proof, side by side" sub="Same room, same camera angle — what we walked into and what we left behind. Renderings shown are representative of typical project scope." />
            <Reveal delay={0.1}>
              <Link to="/projects" data-testid="home-projects-link" className="mt-6 inline-flex h-11 px-5 items-center gap-2 rounded-[10px] border border-[#3E6F8E]/40 bg-[#FFFCF6] text-sm font-medium text-[#3E6F8E] hover:border-[#3E6F8E] hover:bg-white transition-colors duration-200">
                See the before &amp; after gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* 05 — SERVICE AREA */}
      <Section className="bg-[#EDE9E0] overflow-hidden" data-testid="home-service-area">
        <Watermark num="05" />
        <SectionTitle title="Two offices. One standard. All of Puget Sound." />
        <div className="mt-8 max-md:mt-6">
          <CityTicker cities={TICKER_CITIES} />
        </div>
        <div className="mt-8 max-md:mt-5 grid lg:grid-cols-5 gap-5 max-md:gap-4 items-stretch">
          <div className="lg:col-span-2">
            <Reveal className="h-full">
              <ServiceRadiusMap className="h-full min-h-[420px]" />
            </Reveal>
          </div>
          <div className="lg:col-span-3 grid gap-5 max-md:gap-4 content-start">
            {[
              { name: 'Federal Way HQ — South Sound', to: '/service-areas/federal-way', phoneNode: <a href={COMPANY.phoneMainHref} onClick={() => trackCall('home_area_fw')} className="font-semibold text-[#3E6F8E]">{COMPANY.phoneMain}</a>, addr: `${COMPANY.addressFederalWay}, Federal Way, WA`, radius: 'A 60-mile radius across the South Sound — King and Pierce counties and the I-5 corridor.', img: IMAGES.federalWay, alt: 'Seattle skyline with Mount Rainier, the South Sound region served by our Federal Way HQ' },
              { name: 'Snohomish Branch — North Sound', to: '/service-areas/snohomish', phoneNode: <a href={COMPANY.phoneMainHref} onClick={() => trackCall('home_area_sno')} className="font-semibold text-[#3E6F8E]">{COMPANY.phoneMain}</a>, addr: 'North Sound crews, by appointment', radius: 'A 60-mile radius across the North Sound — Snohomish County and the northern corridor.', img: IMAGES.snohomish, alt: 'Sunset over the Snohomish River estuary and Puget Sound, the North Sound region served by our branch crews' },
            ].map((o, i) => (
              <Reveal key={o.name} delay={i * 0.08}>
                <div className="idb-card idb-card-hover overflow-hidden h-full sm:flex">
                  {/* Phones: a 112px banner instead of 160px (mobile polish §6); sm and up unchanged. */}
                  <img src={o.img} srcSet={srcSetFor(o.img)} sizes="(max-width: 640px) 100vw, 208px" {...dims(o.img)} alt={o.alt} loading="lazy" className="h-28 sm:h-auto sm:w-52 w-full object-cover shrink-0" />
                  <div className="p-6 max-sm:p-5">
                    <h3 className="font-display font-semibold text-[#121417] inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#3E6F8E]" /> {o.name}</h3>
                    <p className="mt-2 text-sm text-[#454B52]">{o.addr} · {o.phoneNode}</p>
                    <div className="mt-3 idb-dimension-rule max-sm:hidden" />
                    <p className="mt-3 max-sm:mt-2 text-sm leading-relaxed text-[#61676E]">{o.radius}</p>
                    <Link to={o.to} className="mt-4 max-sm:mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#3E6F8E] hover:underline decoration-[rgba(208,122,74,0.85)] decoration-2 underline-offset-4 max-md:min-h-11" data-testid={`home-area-link-${i}`}>Office details <ArrowRight className="h-4 w-4" /></Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* 06 — GUIDES (the three field-written articles; real <a href> links, mirrored in the pre-render snapshot) */}
      <Section className="overflow-hidden" data-testid="home-guides">
        <Watermark num="06" />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 max-md:gap-4">
          <SectionTitle title="Guides from the field" sub="The same explanations we give at kitchen tables — heat pump versus furnace, what installations really cost here, and how a gas-to-electric conversion works — written down with real Puget Sound numbers." />
          <Link to="/blog" data-testid="home-guides-all-link" className="inline-flex h-11 px-5 items-center gap-2 rounded-[10px] border border-[#3E6F8E]/40 bg-[#FFFCF6] text-sm font-medium text-[#3E6F8E] hover:border-[#3E6F8E] hover:bg-white transition-colors duration-200 shrink-0 self-start lg:self-auto">
            See all guides <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 max-md:mt-6 grid md:grid-cols-3 gap-6 max-md:gap-3">
          {ARTICLE_INDEX.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.07}>
              {/* Under 768px: horizontal list card — 96px thumbnail left, title + one-line excerpt
                  right, ~120px tall (mobile polish §6). From md up: the original vertical card. */}
              <Link to={articlePath(a.slug)} data-testid={`home-guide-card-${i}`} className="group idb-card idb-card-hover overflow-hidden flex flex-col h-full max-md:flex-row max-md:items-center max-md:gap-3 max-md:p-3 max-md:min-h-[120px]">
                <img src={GUIDE_IMAGES[i]} srcSet={srcSetFor(GUIDE_IMAGES[i])} sizes={SIZES.third} {...dims(GUIDE_IMAGES[i])} alt="" aria-hidden="true" loading="lazy" className="h-40 w-full object-cover max-md:h-24 max-md:w-24 max-md:shrink-0 max-md:rounded-[10px]" />
                <div className="p-6 flex flex-col flex-1 max-md:p-0 max-md:min-w-0">
                  <div className="flex items-center gap-4 text-xs text-[#61676E] max-md:hidden">
                    <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Guide</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {a.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-display font-semibold text-[17px] leading-snug text-[#121417] group-hover:text-[#3E6F8E] transition-colors max-md:mt-0 max-md:text-[15px] max-md:line-clamp-2">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#454B52] flex-1 max-md:mt-1.5 max-md:flex-none max-md:text-[13px] max-md:leading-snug max-md:line-clamp-1">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#3E6F8E] max-md:mt-1.5 max-md:text-[13px]">Read the guide <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 07 — FINANCING BAND (HFS brief §5): slim band above the final call-to-action, both lender buttons from config */}
      <section className="relative bg-[#F4F1EA] border-y border-[#D7D2C8]" data-testid="home-financing-band">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="grid lg:grid-cols-[1fr,520px] gap-6 lg:gap-10 items-center">
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-[#121417]"><CreditCard className="h-4 w-4 text-[#3E6F8E]" aria-hidden="true" /> Financing</p>
              <p className="mt-2 text-[15px] sm:text-base leading-relaxed text-[#454B52] max-w-2xl" data-testid="home-financing-text">{HOME_BAND_TEXT}</p>
              <FinancingLink className="mt-2" testId="home-financing-how-link" />
            </div>
            <LenderButtons placement="home_band" testId="home-lenders" />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
